/**
 * Hospital Service - DEPRECATED (Epic 17 - Story 17.2)
 *
 * ⚠️ DEPRECATED: Este service é mantido apenas para compatibilidade retroativa.
 * Novos componentes devem usar `estabelecimentoService.ts` diretamente.
 *
 * Este wrapper redireciona todas as chamadas para estabelecimentoService
 * mantendo a interface antiga para não quebrar componentes existentes.
 *
 * Migration Path:
 * 1. Componentes existentes continuam funcionando (usam este wrapper)
 * 2. Novos componentes usam estabelecimentoService diretamente
 * 3. Migração gradual de componentes antigos
 * 4. Remoção deste arquivo quando todos componentes migrarem
 */
import estabelecimentoService from './estabelecimentoService';
import type { Estabelecimento } from '../types/estabelecimento';

// Type alias para compatibilidade
export type Hospital = Estabelecimento;

// #7 (Rodada 3): contato adicional do hospital (model Contato no backend)
export interface HospitalContato {
  id?: number;        // presente = atualiza; ausente = cria
  nome: string;
  cargo?: string;
  tipo?: string;      // GERAL | COMPRAS | FINANCEIRO | TECNICO
  telefone?: string;
  celular?: string;
  email?: string;
  observacoes?: string;
  ativo?: boolean;    // false = remover (desativa só este)
}

export interface HospitalInput {
  codigo: string;
  razao_social?: string;
  nome_fantasia: string;
  pessoa?: 'FISICA' | 'JURIDICA';
  cnpj?: string;
  inscricao_estadual?: string;
  inscricao_municipal?: string;

  // Endereço simplificado
  endereco?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;

  // Contatos
  contato?: string;
  telefone?: string;
  ramal?: string;
  email?: string;

  // Operacionais
  periodicidade?: string;
  observacoes?: string;
  pontos_contagens?: string;
  reagendamento?: string;

  ativo?: boolean;

  // #7: contatos adicionais (N por hospital). None/undefined = não mexe.
  contatos?: HospitalContato[];
}

class HospitalService {
  /**
   * ⚠️ DEPRECATED: Use estabelecimentoService.listEstabelecimentos()
   */
  async listHospitais(): Promise<Hospital[]> {
    console.warn('⚠️ DEPRECATED: hospitalService.listHospitais() - Use estabelecimentoService.listEstabelecimentos()');
    return estabelecimentoService.listEstabelecimentos();
  }

  /**
   * ⚠️ DEPRECATED: Use estabelecimentoService.getEstabelecimento()
   */
  async getHospital(id: number): Promise<Hospital> {
    console.warn('⚠️ DEPRECATED: hospitalService.getHospital() - Use estabelecimentoService.getEstabelecimento()');
    return estabelecimentoService.getEstabelecimento(id);
  }

  /**
   * ⚠️ DEPRECATED: Use estabelecimentoService.createEstabelecimento()
   */
  async createHospital(data: HospitalInput): Promise<Hospital> {
    console.warn('⚠️ DEPRECATED: hospitalService.createHospital() - Use estabelecimentoService.createEstabelecimento()');
    return estabelecimentoService.createEstabelecimento(data);
  }

  /**
   * ⚠️ DEPRECATED: Use estabelecimentoService.updateEstabelecimento()
   */
  async updateHospital(id: number, data: HospitalInput): Promise<Hospital> {
    console.warn('⚠️ DEPRECATED: hospitalService.updateHospital() - Use estabelecimentoService.updateEstabelecimento()');
    return estabelecimentoService.updateEstabelecimento(id, data);
  }

  /**
   * ⚠️ DEPRECATED: Use estabelecimentoService.deleteEstabelecimento()
   */
  async deleteHospital(id: number): Promise<{ success: boolean; message: string }> {
    console.warn('⚠️ DEPRECATED: hospitalService.deleteHospital() - Use estabelecimentoService.deleteEstabelecimento()');
    return estabelecimentoService.deleteEstabelecimento(id);
  }

  /**
   * Valida CNPJ/CPF com dígitos verificadores
   */
  validateCNPJ(cnpj: string): boolean {
    const clean = cnpj.replace(/\D/g, '');

    if (clean.length === 14) return this._validateCNPJDigits(clean);
    if (clean.length === 11) return this._validateCPFDigits(clean);
    return false;
  }

  private _validateCNPJDigits(cnpj: string): boolean {
    if (/^(\d)\1{13}$/.test(cnpj)) return false;

    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    let sum = 0;
    for (let i = 0; i < 12; i++) sum += parseInt(cnpj[i]) * weights1[i];
    let remainder = sum % 11;
    const digit1 = remainder < 2 ? 0 : 11 - remainder;
    if (parseInt(cnpj[12]) !== digit1) return false;

    sum = 0;
    for (let i = 0; i < 13; i++) sum += parseInt(cnpj[i]) * weights2[i];
    remainder = sum % 11;
    const digit2 = remainder < 2 ? 0 : 11 - remainder;
    return parseInt(cnpj[13]) === digit2;
  }

  private _validateCPFDigits(cpf: string): boolean {
    if (/^(\d)\1{10}$/.test(cpf)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(cpf[i]) * (10 - i);
    let remainder = sum % 11;
    const digit1 = remainder < 2 ? 0 : 11 - remainder;
    if (parseInt(cpf[9]) !== digit1) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(cpf[i]) * (11 - i);
    remainder = sum % 11;
    const digit2 = remainder < 2 ? 0 : 11 - remainder;
    return parseInt(cpf[10]) === digit2;
  }

  /**
   * Formata CNPJ/CPF para exibição
   */
  formatCNPJ(cnpj: string): string {
    const clean = cnpj.replace(/\D/g, '');

    // CNPJ: 14 dígitos
    if (clean.length === 14) {
      return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }

    // CPF: 11 dígitos
    if (clean.length === 11) {
      return clean.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }

    return cnpj;
  }

  /**
   * Formata telefone para exibição
   */
  formatTelefone(telefone: string): string {
    const clean = telefone.replace(/\D/g, '');

    // Celular: (XX) XXXXX-XXXX
    if (clean.length === 11) {
      return clean.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }

    // Fixo: (XX) XXXX-XXXX
    if (clean.length === 10) {
      return clean.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }

    return telefone;
  }
}

export default new HospitalService();
